<template>
  <div id="Canvas"></div>

  <section id="loading-screen">
    <div id="loader"></div>
    <div id="loader-text">0%</div>
  </section>

  <div id="ProgressBar">
    <el-row>
      <el-slider
        v-model="cur_frame"
        :max="num_frames"
        size="small"
        @input="setFrame"
      />
      <div class="frame-text">{{ cur_frame + 1 }} / {{ num_frames }}</div>
    </el-row>
  </div>

  <div id="Buttons">
    <el-row style="justify-content: center">
      <el-button type="primary" color="##262727" size="large" @click="next()">
        Next<el-icon class="el-icon--right" :size="17">
          <ArrowRight />
        </el-icon>
      </el-button>
      <el-button
        v-show="isPause"
        type="primary"
        color="##262727"
        size="large"
        style="margin-left: 10px"
        @click="play()"
      >
        Play<el-icon class="el-icon--right" :size="17">
          <VideoPlay />
        </el-icon>
      </el-button>
      <el-button
        v-show="!isPause"
        type="primary"
        color="##262727"
        size="large"
        style="margin-left: 10px"
        @click="pause()"
      >
        Pause<el-icon class="el-icon--right" :size="17">
          <VideoPause />
        </el-icon>
      </el-button>

      <el-button
        type="primary"
        color="##262727"
        size="large"
        style="margin-left: 10px"
        @click="restart"
      >
        Restart<el-icon class="el-icon--right" :size="17">
          <RefreshLeft />
        </el-icon>
      </el-button>

      <el-button
        v-show="!isSynthesising"
        id="SynthesisBtn"
        type="primary"
        color="##262727"
        size="large"
        style="margin-left: 10px"
        @click="synthesis"
      >
        Generate<el-icon class="el-icon--right" :size="17">
          <MagicStick />
        </el-icon>
      </el-button>
      <el-button
        v-show="isSynthesising"
        id="SynthesisBtn"
        type="primary"
        color="##262727"
        size="large"
        style="margin-left: 10px"
        loading
      >
        Running...
      </el-button>
    </el-row>
  </div>
</template>

<script type="module">
import { ElMessage } from "element-plus";
import {
  ArrowRight,
  RefreshLeft,
  VideoPlay,
  VideoPause,
  MagicStick,
} from "@element-plus/icons-vue";

import { h } from "vue";
import { client } from "@gradio/client";
import * as THREE from "three";
import MySkeletonHelper from "./MySkeletonHelper"; // rewrite SkeletonHelper to support Chrome on MacOS
import Stats from "three/examples/jsm/libs/stats.module.js";
import { GUI } from "three/examples/jsm/libs/lil-gui.module.min.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";

// base scene and model
const fbx_models = [
  "/GenMM_demo/assets/Gangnam_Style.fbx",
  "/GenMM_demo/assets/Wave_Hip_Hop_Dance.fbx",
  "/GenMM_demo/assets/Hip_Hop_Dancing.fbx",
  "/GenMM_demo/assets/Samba_Dancing.fbx",
  "/GenMM_demo/assets/Swing_Dancing.fbx",
];
let cur_fbx_model = 0;
let camera, scene, renderer, stats, panel;
let hemiLight, dirLight, spotLight, ground;
let mixers = [],
  meshes = [],
  skeletons = [],
  durations = [],
  duration_delta;
const clock = new THREE.Clock();
const near = 0.1,
  far = 50; // camera setting
const ground_size = 50; // ground size
const scene_setting = {
  fog: 0.08,
  hemi_light: true,
  dir_light: true,
  spot_light: false,
  ground: true,
};
const visibility_setting = {
  show_mesh: true,
  show_skeleton: false,
  skeleton_width: 5,
};
const api_urls = [
  "https://wyysf-genmm.hf.space/",
  "https://wyysf-genmm-test.hf.space/"
];
let _url_id = Math.floor(Math.random() * api_urls.length);;
// for synthesis
const synthesis_setting = {
  api_url: api_urls[_url_id],
  frames: 500,
  noise_sigma: 25.0,
  loop: false,
  completeness: false,
  alpha: 0.1,
  patch_size: 15,
  coarse_ratio: 0.2,
  pyr_factor: 0.75,
  num_steps: 3,
};

export default {
  name: "MainScene",
  props: {
    init_file: {
      type: String,
      default: fbx_models[0],
    },
    scale: {
      type: Number,
      default: 0.01,
    },
  },
  data: function () {
    return {
      cur_frame: -1,
      num_frames: 0,
      isPause: false,
      isSynthesising: false,
      uploadFilename: null,
      uploadFiles: [],
    };
  },
  mounted() {
    this.initScene();
    this.loadFBXModel(this.init_file);
    this.animate();
  },
  methods: {
    initScene() {
      function create_scene() {
        const container = document.getElementById("Canvas");

        camera = new THREE.PerspectiveCamera(
          45,
          window.innerWidth / window.innerHeight,
          near,
          far
        );
        // camera.position.set(-2, 2, 4.2);
        camera.position.set(0, 2.5, 5);

        scene = new THREE.Scene();
        scene.background = new THREE.Color(0x424242);
        scene.fog = new THREE.FogExp2(0x424242, scene_setting.fog);

        hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444);
        hemiLight.position.set(0, 2, 0);
        scene.add(hemiLight);

        dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
        dirLight.position.set(0, 5, 4);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.width = 2048;
        dirLight.shadow.mapSize.height = 2048;
        scene.add(dirLight);

        spotLight = new THREE.SpotLight();
        spotLight.angle = Math.PI / 8;
        spotLight.penumbra = 0.5;
        spotLight.castShadow = true;
        spotLight.position.set(-0, 5, 3);

        // Ground
        let loader = new THREE.TextureLoader();
        let texture = loader.load("./assets/checker.png");
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.magFilter = THREE.NearestFilter;
        texture.repeat.set(ground_size, ground_size);
        ground = new THREE.Mesh(
          new THREE.PlaneGeometry(ground_size, ground_size),
          new THREE.MeshStandardMaterial({
            map: texture,
            side: THREE.DoubleSide,
            emissiveIntensity: 1,
          })
        );
        ground.rotation.x = Math.PI * -0.5;
        ground.receiveShadow = true;
        scene.add(ground);

        // renderer
        renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.shadowMap.enabled = true;
        container.appendChild(renderer.domElement);

        // controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.target.set(0, 1, 0);
        controls.mouseButtons = {
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.PAN,
        };
        controls.update();

        window.addEventListener("resize", onWindowResize);

        // Stats
        stats = new Stats();
        container.appendChild(stats.dom);
      }

      function create_panel() {
        panel = new GUI({ width: 220 });
        panel.add(synthesis_setting, "api_url");

        const scene_folder = panel.addFolder("Scene");
        scene_folder
          .add(scene_setting, "hemi_light")
          .onChange(function (value) {
            if (value) {
              scene.add(hemiLight);
            } else {
              scene.remove(hemiLight);
            }
          });
        scene_folder.add(scene_setting, "dir_light").onChange(function (value) {
          if (value) {
            scene.add(dirLight);
          } else {
            scene.remove(dirLight);
          }
        });
        scene_folder
          .add(scene_setting, "spot_light")
          .onChange(function (value) {
            if (value) {
              scene.add(spotLight);
            } else {
              scene.remove(spotLight);
            }
          });
        scene_folder.add(scene_setting, "ground").onChange(function (value) {
          if (value) {
            scene.add(ground);
          } else {
            scene.remove(ground);
          }
        });
        scene_folder
          .add(scene_setting, "fog", 0.0, 0.25, 0.01)
          .onChange(function (value) {
            scene.fog = new THREE.FogExp2(0x424242, value);
          });
        scene_folder.close();

        const visibility_folder = panel.addFolder("Visibility");
        visibility_folder
          .add(visibility_setting, "show_mesh")
          .onChange(function (value) {
            for (let i = 0; i < meshes.length; i++) {
              meshes[i].visible = value;
            }
          });
        visibility_folder
          .add(visibility_setting, "show_skeleton")
          .onChange(function (value) {
            for (let i = 0; i < skeletons.length; i++) {
              skeletons[i].visible = value;
            }
          })
          .listen();
        visibility_folder
          .add(visibility_setting, "skeleton_width", 1, 10, 0.5)
          .onChange(function (value) {
            for (let i = 0; i < skeletons.length; i++) {
              skeletons[i].material.linewidth = value / 1000;
            }
          });
        visibility_folder.close();

        const synthesis_folder = panel.addFolder("Synthesis");
        synthesis_folder.add(synthesis_setting, "frames", 1, 2000, 1).listen();
        synthesis_folder.add(synthesis_setting, "noise_sigma", 0.0, 50.0, 1);
        synthesis_folder.add(synthesis_setting, "loop");
        synthesis_folder
          .add(synthesis_setting, "completeness")
          .onChange(function () {
            if (synthesis_setting.completeness) {
              alpha_setting.show();
            } else {
              alpha_setting.hide();
            }
          });
        let alpha_setting = synthesis_folder
          .add(synthesis_setting, "alpha", 0.001, 1, 0.001)
          .hide();
        synthesis_folder.add(synthesis_setting, "patch_size", 1, 30, 1);
        synthesis_folder.add(synthesis_setting, "coarse_ratio", 0.1, 1.0, 0.01);
        synthesis_folder.add(synthesis_setting, "pyr_factor", 0.25, 1.0, 0.01);
        synthesis_folder.add(synthesis_setting, "num_steps", 1, 50, 1);
        synthesis_folder.close();

        panel.close();
      }

      function onWindowResize() {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }

      create_scene();
      create_panel();
    },

    reset() {
      for (let i = 0; i < meshes.length; i++) {
        scene.remove(meshes[i]);
      }
      for (let i = 0; i < skeletons.length; i++) {
        scene.remove(skeletons[i]);
      }
      mixers = [];
      (durations = []), (meshes = []);
      (skeletons = []), (durations = []);
    },

    animate() {
      let _this = this;

      requestAnimationFrame(_this.animate);

      const delta = clock.getDelta();
      const max_duration = Math.max(...durations),
        max_duration_index = durations.indexOf(max_duration);
      for (let i = 0; i < mixers.length; i++) {
        if (max_duration === -Infinity || mixers[i].time <= durations[i]) {
          mixers[i].update(delta);
        }
        if (i === max_duration_index) {
          _this.cur_frame = Math.round(
            (mixers[i].time * _this.num_frames) / durations[i]
          );
          _this.cur_frame %= _this.num_frames;
        }
        if (mixers[i].time > max_duration) {
          for (const mixer of mixers) mixer.setTime(0);
        }
      }

      renderer.render(scene, camera);
      stats.update();
    },

    loadFBXobject(object) {
      let _this = this;

      let mixer = new THREE.AnimationMixer(object);
      // console.log(object.animations);
      for (let i = 0; i < object.animations.length; i++) {
        if (object.animations[i].tracks.length == 0) {
          continue;
        }
        object.animations[0] = object.animations[i];
      }
      // console.log(object.animations[0]);
      let action = mixer.clipAction(object.animations[0]);
      action.play();

      object.traverse(function (child) {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      object.scale.multiplyScalar(_this.scale);
      scene.add(object);

      let skeleton = new MySkeletonHelper(object);
      skeleton.material.linewidth = visibility_setting.skeleton_width / 1000;
      skeleton.visible = visibility_setting.show_skeleton;
      scene.add(skeleton);

      // update frames
      // synthesis_setting.frames = object.animations[0].tracks[0].times.length;
      durations.push(object.animations[0].duration);
      duration_delta =
        object.animations[0].duration /
        (object.animations[0].tracks[0].times.length - 1);
      _this.num_frames =
        Math.round(Math.max(...durations) / duration_delta) + 1;

      mixers.push(mixer);
      meshes.push(object);
      skeletons.push(skeleton);
    },

    loadFBXModel(fbx_model) {
      let _this = this;
      function updateProgress(loaded, total) {
        const percentComplete = Math.round((loaded / total) * 100);
        document.getElementById(
          "loader-text"
        ).textContent = `${percentComplete}%`;
      }

      const loadingManager = new THREE.LoadingManager();
      loadingManager.onStart = (url, loaded, total) => {
        const loadingScreen = document.getElementById("loading-screen");
        loadingScreen.style.display = "block";
        loadingScreen.classList.remove("fade-out");
        updateProgress(loaded, total);
      };

      loadingManager.onProgress = (url, loaded, total) => {
        updateProgress(loaded, total);
      };

      loadingManager.onLoad = () => {
        const loadingScreen = document.getElementById("loading-screen");
        loadingScreen.classList.add("fade-out");
        loadingScreen.addEventListener("transitionend", (event) => {
          event.target.style.display = "none";
          event.target.classList.remove("fade-out");
        });
      };

      const loader = new FBXLoader(loadingManager);
      loader.load(fbx_model, _this.loadFBXobject, undefined, (error) => {
        console.error("Error loading FBX model:", error);
      });
    },

    // Buttons
    restart() {
      for (const mixer of mixers) {
        mixer.setTime(0);
      }

      this.cur_frame = 0;
    },

    play() {
      for (const mixer of mixers) mixer.timeScale = 1;
      this.isPause = false;
    },

    next() {
      cur_fbx_model += 1;
      this.reset();
      console.log(fbx_models[cur_fbx_model]);
      this.loadFBXModel(fbx_models[cur_fbx_model]);
      if (cur_fbx_model >= fbx_models.length - 1) cur_fbx_model = -1;
    },

    pause() {
      for (const mixer of mixers) mixer.timeScale = 0;
      this.isPause = true;
    },

    setFrame(cur_frame) {
      const max_duration = Math.max(...durations);
      for (const mixer of mixers) {
        let timeScale = mixer.timeScale;
        mixer.timeScale = 1;
        mixer.setTime((max_duration * cur_frame) / this.num_frames);
        mixer.timeScale = timeScale;
      }
    },

    // Main function
    synthesis() {
      let _this = this;
      _this.isSynthesising = true;

      async function query(data) {
        const app = await client(synthesis_setting.api_url);
        const result = await app.predict("/predict", [JSON.stringify(data)]);
        return result;
      }

      query({
        setting: synthesis_setting,
        tracks: meshes[0].animations[0].toJSON()["tracks"],
        scale: _this.scale,
      }).then((res) => {
        // console.log(res.data[0].time.toFixed(4));
        ElMessage({
          message: h("p", null, [
            h("span", null, "Generated in "),
            h(
              "i",
              { style: "color: red" },
              res.data[0].time.toFixed(4).toString()
            ),
            h("span", null, " s on CPU without any training."),
          ]),
          duration: 3000,
        });

        let syn_mesh = SkeletonUtils.clone(meshes[0]);
        let syn_mixer = new THREE.AnimationMixer(syn_mesh);
        let animation = THREE.AnimationClip.parse(res.data[0]);
        let action = syn_mixer.clipAction(animation);

        action.play();

        syn_mesh.traverse(function (child) {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        scene.add(syn_mesh);

        let skeleton = new MySkeletonHelper(
          syn_mesh,
          new THREE.Color(0xb9394e),
          new THREE.Color(0xdc9042)
        );
        skeleton.material.linewidth = visibility_setting.skeleton_width / 1000;
        skeleton.visible = false;
        scene.add(skeleton);

        for (const mixer of mixers) mixer.setTime(0);
        durations.push(animation.duration);
        _this.num_frames =
          Math.round(Math.max(...durations) / duration_delta) + 1;

        meshes.push(syn_mesh);
        mixers.push(syn_mixer);
        skeletons.push(skeleton);

        // change position
        let pos = [
          [0, 0],
          [-2, -1],
          [-1, -1],
          [0, -1],
          [1, -1],
          [2, -1],
          [-2, -2],
          [-1, -2],
          [0, -2],
          [1, -2],
          [2, -2],
          [-2, -3],
          [-1, -3],
          [0, -3],
          [1, -3],
          [2, -3],
          [-2, -4],
          [-1, -4],
          [0, -4],
          [1, -4],
          [2, -4],
          [-2, -5],
          [-1, -5],
          [0, -5],
          [1, -5],
          [2, -5],
        ];
        for (let i = 0; i < meshes.length; i++) {
          meshes[i].position.x = pos[i][0];
          meshes[i].position.z = pos[i][1];
        }

        _this.isSynthesising = false;
      });
    },
  },
  components: {
    ArrowRight,
    VideoPlay,
    VideoPause,
    RefreshLeft,
    MagicStick,
  },
};
</script>

<style scoped>
#Canvas {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0px;
  right: 0px;
  top: 0px;
  bottom: 0px;
}
#loading-screen {
  display: block;
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  opacity: 1;
  transition: 1s opacity;
}
#loading-screen.fade-out {
  opacity: 0;
}
#loader {
  display: block;
  position: relative;
  left: 50%;
  top: 50%;
  width: 150px;
  height: 150px;
  margin: -75px 0 0 -75px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #cf3e5b;
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
}
#loader:before {
  content: "";
  position: absolute;
  top: 5px;
  left: 5px;
  right: 5px;
  bottom: 5px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #f2c94a;
  -webkit-animation: spin 3s linear infinite;
  animation: spin 3s linear infinite;
}
#loader:after {
  content: "";
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  bottom: 15px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #52a7db;
  -webkit-animation: spin 1.5s linear infinite;
  animation: spin 1.5s linear infinite;
}
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
#loader-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  font-weight: bold;
  color: #000000;
}
#ProgressBar {
  width: 80%;
  position: fixed;
  bottom: 5%;
  margin: 0 auto;
  left: 0;
  right: 0;
}

#ProgressBar .frame-text {
  width: 100%;
  font-size: 15px;
  font-weight: 500;
  color: #343232;
  height: 22px;
  line-height: 22px;
  margin-left: 5px;
}

:deep(.el-slider__bar) {
  background-color: #63c6fa;
}

:deep(.el-slider__runway) {
  background-color: #ffffff;
}

:deep(.el-slider__button) {
  border-color: #63c6fa;
}
</style>
